import React from "react";
import {
    Autocomplete, Box, Button, Chip, CircularProgress, TextField, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useAuth } from "hooks";
import { useLazySearchVendorsQuery } from "store/services/vendorProfileApi";
import { useAddVendorProfileQuotationExcelSendExcelMailMutation, useLazySearchVendorQuotationExcelsQuery } from "store/services/vendorQuotationExcelApi";

const breadcrumbs = [
    { title: "Quotation VendorProfile List", path: "/app/crm/vendor-lead/quotation-list-vendor" },
    { title: "Add Excel Quotation" },
];

const AddVendorQuotationExcelMailScreen = () => {
    const navigate = useNavigate();
    const { viewEntity } = useAuth();
    const entity = viewEntity?.entity?._id;
    const account = viewEntity?.entity?.account?._id;

    const [vendorProfileInput, setVendorProfileInput] = React.useState("");
    const [selectedVendorProfiles, setSelectedVendorProfiles] = React.useState([]);

    const [excelInput, setExcelInput] = React.useState("");
    const [selectedExcels, setSelectedExcels] = React.useState([]);

    const [vendorProfileTouched, setVendorProfileTouched] = React.useState(false);
    const [excelTouched, setExcelTouched] = React.useState(false);

    const [triggerSearchVendorProfiles, { data: vendorProfileData, isFetching: fetchingVendorProfiles }] =
        useLazySearchVendorsQuery();
    const vendorProfileOptions = vendorProfileData?.data ?? [];

    const [triggerSearchExcels, { data: excelOptions = [], isFetching: fetchingExcels }] =
        useLazySearchVendorQuotationExcelsQuery();

    const [addExcelMail, { isLoading: submitting }] =
        useAddVendorProfileQuotationExcelSendExcelMailMutation();

    React.useEffect(() => {
        if (vendorProfileInput.length < 3) return;
        const t = setTimeout(() => triggerSearchVendorProfiles({ account, entity, query: vendorProfileInput }), 300);
        return () => clearTimeout(t);
    }, [account, entity, vendorProfileInput, triggerSearchVendorProfiles]);

    React.useEffect(() => {
        if (excelInput.length < 3) return;
        const t = setTimeout(() => triggerSearchExcels({ q: excelInput }), 300);
        return () => clearTimeout(t);
    }, [excelInput, triggerSearchExcels]);

    const handleSubmit = async () => {
        setVendorProfileTouched(true);
        setExcelTouched(true);
        if (!selectedVendorProfiles.length || !selectedExcels.length) return;

        try {
            await Promise.all(
                selectedVendorProfiles.flatMap((vendorProfile) =>
                    selectedExcels.map((excel) =>
                        addExcelMail({
                            vendorId: vendorProfile._id,
                            vendorQuotationExcelId: excel._id,
                            account,
                            entity
                        }).unwrap()
                    )
                )
            );
            toast.success("Excel quotation email(s) sent successfully");
            navigate("/app/crm/vendor-lead/quotation-list-vendor");
        } catch (err) {
            toast.error(err?.data?.message ?? err?.message ?? "Could not send email");
        }
    };

    const chipSx = {
        bgcolor: "#2a2a2a",
        color: "#e0e0e0",
        border: "1px solid #444",
        fontWeight: 400,
        "& .MuiChip-deleteIcon": { color: "#888", "&:hover": { color: "#bbb" } },
    };

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>
                <Box sx={{ maxWidth: 560, mx: "auto", px: 3, py: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                    <Typography variant="h6" fontWeight={600}>
                        Send Excel Quotation to VendorProfile
                    </Typography>

                    {/* VendorProfile multi-select */}
                    <Autocomplete
                        multiple
                        options={vendorProfileOptions}
                        getOptionLabel={(o) => `${o.shopName ?? ""} - ${o.ownerName ?? ""}`}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        loading={fetchingVendorProfiles}
                        filterOptions={(x) => x}
                        inputValue={vendorProfileInput}
                        value={selectedVendorProfiles}
                        onInputChange={(_, val, reason) => {
                            if (reason !== "reset") setVendorProfileInput(val);
                        }}
                        onChange={(_, val) => {
                            setSelectedVendorProfiles(val);
                            setVendorProfileTouched(true);
                            setVendorProfileInput("");
                        }}
                        noOptionsText={vendorProfileInput.length < 3 ? "Type at least 3 characters to search" : "No vendorProfiles found"}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option._id}
                                    label={`${option.shopName ?? ""} - ${option.ownerName ?? ""}`}
                                    size="small"
                                    sx={chipSx}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select VendorProfile(s)"
                                placeholder={selectedVendorProfiles.length ? "Add more…" : "Search by name or ID…"}
                                required
                                error={vendorProfileTouched && !selectedVendorProfiles.length}
                                helperText={vendorProfileTouched && !selectedVendorProfiles.length ? "At least one Vendor Profile is required" : " "}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {fetchingVendorProfiles && <CircularProgress color="inherit" size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Excel multi-select */}
                    <Autocomplete
                        multiple
                        options={excelOptions}
                        getOptionLabel={(o) => `${o.title ?? ""} - ${o.VendorQuotationExcelId ?? ""}`}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        loading={fetchingExcels}
                        filterOptions={(x) => x}
                        inputValue={excelInput}
                        value={selectedExcels}
                        onInputChange={(_, val, reason) => {
                            if (reason !== "reset") setExcelInput(val);
                        }}
                        onChange={(_, val) => {
                            setSelectedExcels(val);
                            setExcelTouched(true);
                            setExcelInput("");
                        }}
                        noOptionsText={excelInput.length < 3 ? "Type at least 3 characters to search" : "No excel quotations found"}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option._id}
                                    label={`${option.title ?? ""} - ${option.VendorQuotationExcelId ?? ""}`}
                                    size="small"
                                    sx={chipSx}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Excel Quotation(s)"
                                placeholder={selectedExcels.length ? "Add more…" : "Search by title or ID…"}
                                required
                                error={excelTouched && !selectedExcels.length}
                                helperText={excelTouched && !selectedExcels.length ? "At least one excel quotation is required" : " "}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {fetchingExcels && <CircularProgress color="inherit" size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Actions */}
                    <Box display="flex" gap={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/app/crm/vendor-lead/quotation-list-vendor")}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            variant="contained"
                            color="success"
                            loading={submitting}
                            onClick={handleSubmit}
                        >
                            Send Excel Email
                        </LoadingButton>
                    </Box>
                </Box>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default AddVendorQuotationExcelMailScreen;